import { Col, Row } from "react-grid-system";
import Container from "../common/Container";
import StyledButton from "../common/buttons/StyledButton";
import styles from "./Notes.module.css";
import toolStyles from "../../sharedStyles/Tool.module.css";
import { toolTypeToName } from "../AccountDetails/utils";
import { ToolType } from "../AccountDetails";
import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import RoundButton from "../common/buttons/RoundButton";
import { useGetNotesSearch } from "../../lib/api/queries";
import Select from "../common/inputs/Select";
import { DateString, Note, ValidationErrors } from "../../lib/api/models";
import DateInput from "../common/inputs/DateInput";
import StyledInput from "../common/inputs/StyledInput";
import Spinner from "../common/Spinner";
import ToolLoadError from "../common/ToolLoadError";
import { getDisplayError } from "../../lib/api/endpoints";
import NotesTable from "./NotesTable";
import { formatToDateStringInCurrentTimezone } from "../../lib/formatting";
import NoteDetails from "./NoteDetails";
import { differenceInMonths } from "date-fns";
import WarningModal from "../common/modals/WarningModal/WarningModal";
import { dateLimitation } from "../../lib/constants/dates";

const strings = {
  close: "Close",
  critical: "Critical",
  expanded: "Expanded",
  detail: "Detail",
  startDate: "Start Date",
  endDate: "End Date",
  agent: "Agent",
  channel: "Channel",
  source: "Source",
  content: "Content",
  searchNotes: "Search Notes",
  all: "All",
  allBracket: "<All>",
  allSelected: "All Selected",
  dateFilterWarningModalLabel: "Date Filter Warning Modal",
  dateFilterWarning:
    "A_0075 - The extended date range in the note's search criteria may cause a delay in the response. Do you want to continue?",
  continue: "Continue",
  cancel: "Cancel",
  noNewNotesWarningModalLabel: "No New Notes Warning Modal",
  noNewNotesWarning:
    "A_0074 - No Critical or Regular notes match current search criteria. Displayed notes have not been refreshed.",
  ok: "OK",
  startDateInvalid:
    "A_0085 - The Start Date entered is not a valid date, Please enter a valid Start Date.",
  endDateInvalid:
    "A_0087 - The End Date entered is not a valid date. Please enter a valid End Date.",
  startDateAfterEndDate:
    "A_0088 - The Start Date must be prior to the End Date, please correct.",
  startDateBeforeDateLimitation:
    "A_0084 - Please enter a Start Date equal to or greater than 1/1/1987.",
  endDateBeforeDateLimitation:
    "A_0086 - Please enter an End Date equal to or greater than 1/1/1987.",
};

type NotesTableContentProps = {
  isNotesSearchLoading: boolean;
  isNotesSearchError: boolean;
  getNotesSearchError: unknown;
  getNotesSearchRefetch: () => void;
  notes: Note[];
  onClickRow: (note: Note) => void;
  notesPriority: "critical" | "expanded";
};

function NotesTableContent({
  isNotesSearchLoading,
  isNotesSearchError,
  getNotesSearchError,
  getNotesSearchRefetch,
  notes,
  onClickRow,
  notesPriority,
}: NotesTableContentProps) {
  return isNotesSearchLoading ? (
    <Row>
      <Col className={styles.spinnerContainer}>
        <Spinner />
      </Col>
    </Row>
  ) : isNotesSearchError ? (
    <Row>
      <Col>
        <ToolLoadError
          toolName={toolTypeToName(ToolType.Notes)}
          errorDescription={getDisplayError(getNotesSearchError)?.message}
          onRetry={getNotesSearchRefetch}
        />
      </Col>
    </Row>
  ) : (
    <NotesTable
      notes={notes}
      onClickRow={onClickRow}
      notesPriority={notesPriority}
    />
  );
}

type Props = {
  className?: string;
  dwbuid: string;
  toolId: string;
  onRequestClose: () => void;
};

export default function Notes({
  className,
  dwbuid,
  toolId,
  onRequestClose,
}: Props) {
  const now = new Date();
  // Use current timezone because the final value is a timeless date string
  // and some timezones can cause one-day differences
  const today = formatToDateStringInCurrentTimezone(
    now,
  ).toString() as DateString;

  const [currentTab, setCurrentTab] = useState<
    "critical" | "expanded" | "details"
  >("critical");
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [showDateFilterWarning, setShowDateFilterWarning] = useState(false);
  const [validationMessages, setValidationMessages] =
    useState<ValidationErrors>();
  // Expanded form filters
  const [startDate, setStartDate] = useState(
    formatToDateStringInCurrentTimezone(
      new Date(now.getFullYear(), now.getMonth() - 1, 1),
    ).toString() as DateString,
  );
  const [endDate, setEndDate] = useState(today);
  const [agent, setAgent] = useState("");
  const [channel, setChannel] = useState("");
  const [source, setSource] = useState("");
  const [content, setContent] = useState("");
  // Form filters for api request
  const [searchStartDate, setSearchStartDate] = useState<string>(
    formatToDateStringInCurrentTimezone(
      new Date(now.getFullYear(), now.getMonth() - 1, 1),
    ).toString() as DateString,
  );
  const [searchEndDate, setSearchEndDate] = useState<string>(today);
  const [searchAgent, setSearchAgent] = useState<string>();
  const [searchChannel, setSearchChannel] = useState<string>();
  const [searchSource, setSearchSource] = useState<string>();
  const [searchContent, setSearchContent] = useState<string>();
  function updateSearchFilters() {
    setSearchAgent(agent === "" ? undefined : agent);
    setSearchChannel(channel === "" ? undefined : channel);
    setSearchContent(content === "" ? undefined : content);
    setSearchEndDate(endDate);
    setSearchSource(source === "" ? undefined : source);
    setSearchStartDate(startDate);
  }
  function submitFilters() {
    // Check for validation messages before submitting
    const valMessages: ValidationErrors = {};
    if (startDate === "") {
      valMessages.startDate = [strings.startDateInvalid];
    } else if (startDate < dateLimitation) {
      valMessages.startDate = [strings.startDateBeforeDateLimitation];
    }
    if (endDate === "") {
      valMessages.endDate = [strings.endDateInvalid];
    } else if (endDate < dateLimitation) {
      valMessages.endDate = [strings.endDateBeforeDateLimitation];
    }
    if (startDate > endDate) {
      if (!("startDate" in valMessages)) {
        valMessages.startDate = [strings.startDateAfterEndDate];
      } else {
        valMessages.startDate = [
          ...valMessages.startDate,
          strings.startDateAfterEndDate,
        ];
      }
    }
    if (Object.keys(valMessages).length > 0) {
      setValidationMessages(valMessages);
      return;
    }
    setValidationMessages({});
    if (differenceInMonths(new Date(endDate), new Date(startDate)) >= 24) {
      setShowDateFilterWarning(true);
    } else {
      updateSearchFilters();
    }
  }

  useEffect(() => {
    const pressed: Set<string> = new Set();
    const onKeyDown = (e: KeyboardEvent) => {
      pressed.add(e.code);
      // support hotkey Ctrl + ALT + n + c to open critical
      if (
        pressed.size === 4 &&
        e.ctrlKey &&
        e.altKey &&
        pressed.has("KeyN") &&
        pressed.has("KeyC")
      ) {
        e.preventDefault();
        setCurrentTab("critical");
      } else if (
        pressed.size === 4 &&
        e.ctrlKey &&
        e.altKey &&
        pressed.has("KeyN") &&
        pressed.has("KeyE")
      ) {
        // support hotkey Ctrl + ALT + n + e to open expanded
        e.preventDefault();
        setCurrentTab("expanded");
      } else if (
        // support hotkey Ctrl + ALT + n + d to open details
        pressed.size === 4 &&
        e.ctrlKey &&
        e.altKey &&
        pressed.has("KeyN") &&
        pressed.has("KeyD") &&
        selectedNote !== null
      ) {
        e.preventDefault();
        setCurrentTab("details");
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      pressed.delete(e.code);
    };
    const onBlur = () => pressed.clear();

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, [selectedNote]);

  const {
    data: getNotesSearchData,
    isLoading: isNotesSearchLoading,
    isFetching: isNotesSearchFetching,
    isError: isNotesSearchError,
    error: getNotesSearchError,
    refetch: getNotesSearchRefetch,
  } = useGetNotesSearch({
    dwbuid: dwbuid,
    notePriorityTypes:
      currentTab === "expanded" ? ["REGULAR", "CRITICAL"] : ["CRITICAL"],
    agent: currentTab === "expanded" ? searchAgent : undefined,
    channel: currentTab === "expanded" ? searchChannel : undefined,
    endDate: currentTab === "expanded" ? searchEndDate : undefined,
    noteContent: currentTab === "expanded" ? searchContent : undefined,
    noteSourceType: currentTab === "expanded" ? searchSource : undefined,
    startDate: currentTab === "expanded" ? searchStartDate : undefined,
  });

  const sourceTypeOptions = useMemo(() => {
    if (!getNotesSearchData) {
      return [];
    }

    return [
      { value: "", label: strings.all },
      ...getNotesSearchData.sourceTypeOptions.map((option) => {
        return { value: option.id, label: option.value };
      }),
    ];
  }, [getNotesSearchData]);
  const channelOptions = useMemo(() => {
    if (!getNotesSearchData) {
      return [];
    }

    return [
      { value: "", label: strings.allSelected },
      ...getNotesSearchData.channelOptions.map((option) => {
        return { value: option.id, label: option.value };
      }),
    ];
  }, [getNotesSearchData]);

  const initialExpandedNotesLoadedRef = useRef(false);
  const prevExpandedNotesRef = useRef<Note[] | null>(null);
  const [showNoNewNotesWarning, setShowNoNewNotesWarning] = useState(false);
  const [criticalNotes, setCriticalNotes] = useState<Note[]>([]);
  const [expandedNotes, setExpandedNotes] = useState<Note[]>([]);
  useEffect(() => {
    if (!getNotesSearchData || isNotesSearchLoading || isNotesSearchError) {
      return;
    }

    if (currentTab === "details") {
      return;
    }

    if (currentTab === "critical") {
      setCriticalNotes(getNotesSearchData.notes);
      return;
    }

    if (!initialExpandedNotesLoadedRef.current) {
      prevExpandedNotesRef.current = getNotesSearchData.notes;
      setExpandedNotes(getNotesSearchData.notes);
      initialExpandedNotesLoadedRef.current = true;
      return;
    }

    const newNotes = getNotesSearchData.notes;
    if (newNotes.length) {
      prevExpandedNotesRef.current = newNotes;
      setExpandedNotes(newNotes);
    } else if (prevExpandedNotesRef.current?.length) {
      setShowNoNewNotesWarning(true);
    }
  }, [
    getNotesSearchData,
    isNotesSearchLoading,
    isNotesSearchError,
    currentTab,
  ]);

  return (
    <div className={classNames(toolStyles.container, className)} id={toolId}>
      <Container>
        <Row
          className={classNames(
            toolStyles.header,
            toolStyles.headerWithDivider,
          )}
        >
          <Col md={1} className={toolStyles.centerCol}>
            <h2 className={toolStyles.title}>
              {toolTypeToName(ToolType.Notes)}
            </h2>
          </Col>
          <Col md={9}>
            <RoundButton
              className={styles.tabButton}
              variant={currentTab === "critical" ? "blue" : "grey"}
              onClick={() => setCurrentTab("critical")}
            >
              {strings.critical}
            </RoundButton>
            <RoundButton
              className={styles.tabButton}
              variant={currentTab === "expanded" ? "blue" : "grey"}
              onClick={() => setCurrentTab("expanded")}
            >
              {strings.expanded}
            </RoundButton>
            {selectedNote !== null && (
              <RoundButton
                className={styles.tabButton}
                variant={currentTab === "details" ? "blue" : "grey"}
                onClick={() => setCurrentTab("details")}
              >
                {strings.detail}
              </RoundButton>
            )}
          </Col>
          <Col offset={{ md: 1 }} md={1}>
            <StyledButton
              variant="secondary"
              className={toolStyles.headerButton}
              onClick={onRequestClose}
            >
              {strings.close}
            </StyledButton>
          </Col>
        </Row>
        {currentTab === "expanded" && (
          <>
            <Row className={styles.filtersRow}>
              <Col md={2}>
                <DateInput
                  label={strings.startDate}
                  value={startDate}
                  onChangeDate={(val) => {
                    setStartDate(val);
                  }}
                  onBlur={() => {
                    if (startDate > today) {
                      setStartDate(today);
                    }
                  }}
                  max={today}
                  showPopover={false}
                  validationMessages={validationMessages?.startDate}
                />
              </Col>
              <Col md={2}>
                <DateInput
                  label={strings.endDate}
                  value={endDate}
                  onChangeDate={(val) => {
                    setEndDate(val);
                  }}
                  onBlur={() => {
                    if (endDate > today) {
                      setEndDate(today);
                    }
                  }}
                  max={today}
                  showPopover={false}
                  validationMessages={validationMessages?.endDate}
                />
              </Col>
              <Col md={1}>
                <StyledInput
                  label={strings.agent}
                  value={agent}
                  onChange={(e) => {
                    setAgent(e.target.value);
                  }}
                  placeholder={strings.allBracket}
                />
              </Col>
              <Col md={2}>
                <Select
                  selectedValue={channel}
                  options={channelOptions}
                  onChange={setChannel}
                  label={strings.channel}
                />
              </Col>
              <Col md={1}>
                <Select
                  selectedValue={source}
                  options={sourceTypeOptions}
                  onChange={setSource}
                  label={strings.source}
                />
              </Col>
              <Col md={2}>
                <StyledInput
                  label={strings.content}
                  value={content}
                  onChange={(e) => {
                    setContent(e.target.value);
                  }}
                  placeholder={strings.allBracket}
                />
              </Col>
              <Col md={2} className={styles.searchNotesContainer}>
                <StyledButton
                  variant="secondary"
                  className={styles.searchNotesButton}
                  onClick={submitFilters}
                  isLoading={isNotesSearchFetching}
                  disabled={
                    ((!agent && !searchAgent) || agent === searchAgent) &&
                    ((!channel && !searchChannel) ||
                      channel === searchChannel) &&
                    ((!content && !searchContent) ||
                      content === searchContent) &&
                    endDate === searchEndDate &&
                    startDate === searchStartDate &&
                    ((!source && !searchSource) || source === searchSource)
                  }
                >
                  {strings.searchNotes}
                </StyledButton>
              </Col>
            </Row>
          </>
        )}
        {(currentTab === "critical" || currentTab === "expanded") && (
          <NotesTableContent
            notes={currentTab === "critical" ? criticalNotes : expandedNotes}
            isNotesSearchError={isNotesSearchError}
            isNotesSearchLoading={isNotesSearchLoading}
            getNotesSearchRefetch={getNotesSearchRefetch}
            getNotesSearchError={getNotesSearchError}
            onClickRow={(note: Note) => {
              if (note.detailId) {
                setCurrentTab("details");
                setSelectedNote(note);
              }
            }}
            notesPriority={currentTab === "critical" ? "critical" : "expanded"}
          />
        )}
        {currentTab === "details" &&
          selectedNote !== null &&
          selectedNote.detailId !== null && (
            <NoteDetails
              dwbuid={dwbuid}
              noteId={selectedNote.detailId}
              noteContent={selectedNote.content}
              noteDate={selectedNote.noteDate}
              accountNumberLast={selectedNote.accountNumberLast}
            />
          )}
        <WarningModal
          isOpen={showDateFilterWarning}
          modalLabel={strings.dateFilterWarningModalLabel}
          body={strings.dateFilterWarning}
          confirmText={strings.continue}
          onConfirm={() => {
            setShowDateFilterWarning(false);
            updateSearchFilters();
          }}
          closeText={strings.cancel}
          onRequestClose={() => {
            setShowDateFilterWarning(false);
          }}
        />
        <WarningModal
          isOpen={showNoNewNotesWarning}
          modalLabel={strings.noNewNotesWarningModalLabel}
          body={strings.noNewNotesWarning}
          closeText={strings.ok}
          onRequestClose={() => setShowNoNewNotesWarning(false)}
        />
      </Container>
    </div>
  );
}
